import React, { useEffect, useMemo, useState } from "react";
import ReactDom from "react-dom";

// Mesma casca visual do modal de inadimplência; aqui só entram os ajustes próprios.
import base from "../delinquencyPredictedModal/delinquencyPredictedModal.module.css";
import styles from "./dropoutRiskModal.module.css";
import Button from "../../button/button";
import { addPorcentage, formatNumberToCurrency, formatShortDate } from "../../../utils/formatter";
import { downloadAsCsv, downloadAsExcel } from "../../../utils/tableExport";
import {
  chanceTooltip,
  describeCalibration,
  describeMissingCalibration,
  formatChance,
  horizonLabel,
  isRetroEstimate,
  probabilityForStudent
} from "../../../utils/dropoutProbability";

import DownloadIcon from "../../../assets/icons/download.svg";

const RISK_LABEL = { alto: "Alto", medio: "Médio", baixo: "Baixo" };
const FILE_BASE = "alunos-risco-evasao";

const round2 = (value) => Math.round(Number(value || 0) * 100) / 100;
const round1 = (value) => (value == null ? "" : Math.round(Number(value) * 10) / 10);

// ReasonsJson vem como texto; cada item pode ser string ou objeto {description|reason}.
export const parseReasons = (reasonsJson) => {
  if (!reasonsJson) {
    return [];
  }
  try {
    const parsed = JSON.parse(reasonsJson);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map((item) => {
      if (typeof item === "string") {
        return item;
      }
      return item?.description || item?.reason || JSON.stringify(item);
    });
  } catch {
    return [];
  }
};

// Chance de evadir: probabilidade calibrada do score do aluno (utils/dropoutProbability).
// Null quando não há curva calibrada (a coluna some).
export const rateForStudent = probabilityForStudent;

export const filterStudents = (students, mode) => {
  if (mode === "alto") {
    return students.filter((s) => s.riskBand === "alto");
  }
  return students;
};

const riskClass = (band) => {
  if (band === "alto") {
    return base.riskHigh;
  }
  if (band === "medio") {
    return base.riskMedium;
  }
  return base.riskLow;
};

// Colunas do arquivo baixado: as da tabela mais motivos e data da análise.
export const toExportRows = (students, bandRates) =>
  students.map((s) => {
    const rate = rateForStudent(bandRates, s);
    return {
      "Id do Aluno": s.studentId,
      Curso: s.courseName || "",
      Score: s.matchedCriteriaCount ?? "",
      "Faixa de risco": RISK_LABEL[s.riskBand] || s.riskBand || "",
      "Chance de evadir (%)": rate == null ? "" : Math.round(rate * 100),
      "Frequência (%)": round1(s.avgFrequency),
      "Nota final": round1(s.avgFinalGrade),
      "Parcelas vencidas": s.overdueInstallments ?? 0,
      "Valor vencido (R$)": round2(s.overdueValue),
      Motivos: parseReasons(s.reasonsJson).join("; "),
      "Analisado em": formatShortDate(s.analyzedAt)
    };
  });

// Drill-down dos números do card "Predição · Evasão" da home. A lista já está no
// navegador (a home carrega a primeira página grande de resultados), então não há
// chamada extra: só filtro e download.
const DropoutRiskModal = ({
  students = [],
  total = 0,
  bandRates = null,
  probabilityStatus = null,
  minHighScore = null,
  initialMode = "all",
  onClose
}) => {
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const highRiskCount = useMemo(() => filterStudents(students, "alto").length, [students]);
  const visible = useMemo(() => filterStudents(students, mode), [students, mode]);
  const hasRates = !!bandRates;
  // Taxa média observada: vem da curva quando existe, senão do status (ciclo medido sem curva).
  const baseRate = bandRates?.baseRate ?? probabilityStatus?.baseRate ?? null;
  const horizon = horizonLabel(bandRates?.horizonDays ?? probabilityStatus?.horizonDays);
  const analyzedAt = useMemo(
    () => students.reduce((latest, s) => (s.analyzedAt && (!latest || s.analyzedAt > latest) ? s.analyzedAt : latest), null),
    [students]
  );
  const truncated = total > students.length;

  const fileSuffix = mode === "alto" ? "-alto-risco" : "";
  const exportRows = () => toExportRows(visible, bandRates);

  return ReactDom.createPortal(
    <div className={base.modalBackdrop} onClick={onClose}>
      <div
        className={base.modalContent}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dropout-risk-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={base.modalHeader}>
          <div>
            <span className={`${base.eyebrow} ${styles.eyebrowDanger}`}>PREDIÇÃO · EVASÃO</span>
            <h3 id="dropout-risk-title">Alunos em risco de evasão</h3>
            <span className={base.subtitle}>
              Critérios acadêmicos e financeiros
              {analyzedAt ? ` · análise de ${formatShortDate(analyzedAt)}` : ""}
            </span>
          </div>
          <button type="button" className={base.closeButton} onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>

        <div className={base.meta}>
          <div className={base.metaItem}>
            <span className={base.metaLabel}>Alunos classificados</span>
            <span className={base.metaValue}>{total || students.length}</span>
          </div>
          <div className={base.metaItem}>
            <span className={base.metaLabel}>Alto risco{minHighScore != null ? ` · score ≥ ${minHighScore}` : ""}</span>
            <span className={`${base.metaValue} ${base.metaValueDanger}`}>{highRiskCount}</span>
          </div>
          {baseRate != null && (
            <div className={base.metaItem}>
              <span className={base.metaLabel}>Evasão média · {horizon}</span>
              <span className={base.metaValue}>{formatChance(baseRate)}</span>
            </div>
          )}
          {hasRates && bandRates.alto != null && (
            <div className={base.metaItem}>
              <span className={base.metaLabel}>Evasão observada no alto risco</span>
              <span className={`${base.metaValue} ${base.metaValueDanger}`}>{formatChance(bandRates.alto)}</span>
            </div>
          )}
        </div>

        <div className={styles.toolbar}>
          <div className={styles.segmented} role="tablist" aria-label="Filtro de risco">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "all"}
              className={`${styles.segment} ${mode === "all" ? styles.segmentActive : ""}`}
              onClick={() => setMode("all")}
            >
              Todos · {students.length}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "alto"}
              className={`${styles.segment} ${mode === "alto" ? styles.segmentActive : ""}`}
              onClick={() => setMode("alto")}
            >
              Alto risco · {highRiskCount}
            </button>
          </div>
          {truncated && (
            <span className={styles.truncated}>
              Mostrando os {students.length} de maior score entre {total}. A lista completa está em Possíveis Evasores.
            </span>
          )}
        </div>

        <div className={base.body}>
          {visible.length === 0 ? (
            <div className={base.state}>
              <span className={base.stateText}>Nenhum aluno nesta faixa.</span>
            </div>
          ) : (
            <div className={base.tableWrapper}>
              <table className={base.table}>
                <thead>
                  <tr>
                    <th>Aluno</th>
                    <th>Curso</th>
                    <th>Score</th>
                    {hasRates && (
                      <th title={describeCalibration(bandRates)}>
                        Chance de evadir · {horizon}{isRetroEstimate(bandRates) ? " *" : ""}
                      </th>
                    )}
                    <th>Frequência</th>
                    <th>Nota final</th>
                    <th>Parcelas vencidas</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s) => {
                    const reasons = parseReasons(s.reasonsJson);
                    const rate = rateForStudent(bandRates, s);
                    return (
                      <tr key={s.studentId} title={reasons.length ? reasons.join(" · ") : undefined}>
                        <td className={base.studentId}>{s.studentId}</td>
                        <td>{s.courseName || "-"}</td>
                        <td className={base.numeric}>
                          <span className={`${base.risk} ${riskClass(s.riskBand)}`}>{s.matchedCriteriaCount ?? "-"}</span>
                        </td>
                        {hasRates && (
                          <td className={base.numeric} title={chanceTooltip(bandRates, s)}>
                            {rate == null ? "-" : formatChance(rate)}
                          </td>
                        )}
                        <td className={base.numeric}>{s.avgFrequency != null ? addPorcentage(Number(s.avgFrequency)) : "-"}</td>
                        <td className={base.numeric}>{s.avgFinalGrade != null ? round1(s.avgFinalGrade) : "-"}</td>
                        <td className={base.numeric}>
                          {s.overdueInstallments > 0
                            ? `${s.overdueInstallments} · ${formatNumberToCurrency(Number(s.overdueValue || 0))}`
                            : "nenhuma"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={base.footer}>
          <p className={base.note}>
            O score soma os sinais de alerta de cada aluno (frequência, nota, parcelas vencidas e risco de
            inadimplência); alto risco é score {minHighScore != null ? `≥ ${minHighScore}` : "≥ 6"}.
            {hasRates
              ? ` ${isRetroEstimate(bandRates) ? "* " : ""}${describeCalibration(bandRates)} Passe o mouse sobre o percentual para ver o intervalo.`
              : ""}
            {!hasRates && probabilityStatus ? ` ${describeMissingCalibration(probabilityStatus)}` : ""}
            {" "}Passe o mouse sobre a linha para ver os motivos.
          </p>
          <div className={base.actions}>
            <Button
              icon={DownloadIcon}
              text="CSV"
              variation="outline"
              size="normal"
              disabled={!visible.length}
              onClick={() => downloadAsCsv(exportRows(), `${FILE_BASE}${fileSuffix}.csv`)}
            />
            <Button
              icon={DownloadIcon}
              text="Excel"
              variation="outline"
              size="normal"
              disabled={!visible.length}
              onClick={() => downloadAsExcel(exportRows(), `${FILE_BASE}${fileSuffix}.xlsx`, "Alunos")}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DropoutRiskModal;
